import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import ReactMarkdown from "react-markdown";
import { api, resolveApiUrl } from "../api/client";
import { useRunEvents } from "../api/useRunEvents";

export function RunDetailPage() {
  const { id } = useParams<{ id: string }>();
  const runId = id ?? "";
  const qc = useQueryClient();
  const navigate = useNavigate();

  useRunEvents(runId);

  const [isEditingPremise, setIsEditingPremise] = useState(false);
  const [editedPremise, setEditedPremise] = useState("");
  const [showBibleModal, setShowBibleModal] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [showInsertModal, setShowInsertModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  // Settings state
  const [tempVal, setTempVal] = useState(0.7);
  const [maxReviseVal, setMaxReviseVal] = useState(2);

  // Insert Chapter state
  const [insertNo, setInsertNo] = useState(1);
  const [insertTitle, setInsertTitle] = useState("");
  const [insertContent, setInsertContent] = useState("");

  const run = useQuery({
    queryKey: ["run", runId],
    queryFn: () => api.getRun(runId),
    enabled: !!runId,
    refetchInterval: 3_000,
  });
  const chapters = useQuery({
    queryKey: ["chapters", runId],
    queryFn: () => api.listChapters(runId),
    enabled: !!runId,
    refetchInterval: 3_000,
  });
  const bibleMd = useQuery({
    queryKey: ["bibleMd", runId],
    queryFn: () => api.bibleMarkdown(runId),
    enabled: !!runId && showBibleModal,
  });
  const bibleFull = useQuery({
    queryKey: ["bibleFull", runId],
    queryFn: () => api.getBibleFull(runId),
    enabled: !!runId && showBibleModal,
  });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["run", runId] });
    qc.invalidateQueries({ queryKey: ["runs"] });
    qc.invalidateQueries({ queryKey: ["chapters", runId] });
  };

  const pauseMutation = useMutation({
    mutationFn: () => api.pauseRun(runId),
    onSuccess: invalidate,
  });

  const resumeMutation = useMutation({
    mutationFn: () => api.resumeRun(runId),
    onSuccess: invalidate,
  });

  const cancelMutation = useMutation({
    mutationFn: () => api.cancelRun(runId),
    onSuccess: invalidate,
  });

  const deleteMutation = useMutation({
    mutationFn: () => api.deleteRun(runId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["runs"] });
      navigate("/");
    },
  });

  const generateCoverMutation = useMutation({
    mutationFn: () => api.generateCover(runId),
    onSuccess: invalidate,
  });

  const updateSettingsMutation = useMutation({
    mutationFn: () =>
      api.updateSettings(runId, {
        temperature: tempVal,
        maxRevisePasses: maxReviseVal,
      }),
    onSuccess: () => {
      setShowSettingsModal(false);
      invalidate();
    },
  });

  const insertChapterMutation = useMutation({
    mutationFn: () => api.insertChapter(runId, insertNo, insertTitle, insertContent),
    onSuccess: () => {
      setShowInsertModal(false);
      setInsertTitle("");
      setInsertContent("");
      invalidate();
    },
  });

  const reorderMutation = useMutation({
    mutationFn: (orders: Array<{ id: string; chapterNo: number }>) =>
      api.reorderChapters(runId, orders),
    onSuccess: invalidate,
  });

  const updatePremiseMutation = useMutation({
    mutationFn: () => api.updateRun(runId, { premise: editedPremise }),
    onSuccess: () => {
      setIsEditingPremise(false);
      invalidate();
    },
  });

  if (run.isLoading) {
    return <div className="text-slate-400 text-sm">Loading run details…</div>;
  }
  if (run.error) {
    return (
      <div className="rounded-md bg-rose-950 border border-rose-800 px-4 py-3 text-sm text-rose-200">
        Failed to load run: {(run.error as Error).message}
      </div>
    );
  }
  if (!run.data?.run) {
    return <div className="text-slate-400 text-sm">Run not found.</div>;
  }

  const r = run.data.run;
  const bible = run.data.bible;
  const pct = Math.min(
    100,
    Math.round(((r.currentChapter ?? 0) / Math.max(1, r.targetChapters)) * 100),
  );

  const chapterList = chapters.data?.chapters ?? [];
  const filteredChapters = chapterList.filter((c) => {
    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase();
    return (
      (c.title ?? "").toLowerCase().includes(q) ||
      `chapter ${c.chapterNo}`.includes(q)
    );
  });

  const moveChapter = (index: number, direction: "up" | "down") => {
    if (direction === "up" && index === 0) return;
    if (direction === "down" && index === chapterList.length - 1) return;

    const targetIndex = direction === "up" ? index - 1 : index + 1;
    const itemA = chapterList[index];
    const itemB = chapterList[targetIndex];

    if (!itemA || !itemB) return;

    const orders = [
      { id: itemA.id, chapterNo: itemB.chapterNo },
      { id: itemB.id, chapterNo: itemA.chapterNo },
    ];
    reorderMutation.mutate(orders);
  };

  return (
    <div className="space-y-6">
      <header className="space-y-4 border-b border-slate-800 pb-5">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <Link to="/" className="text-xs text-slate-400 hover:text-slate-200">
            ← All runs
          </Link>

          {/* Control Toolbar */}
          <div className="flex items-center gap-2 flex-wrap">
            {(r.status === "running" || r.status === "checkpoint_pending") && (
              <button
                onClick={() => pauseMutation.mutate()}
                disabled={pauseMutation.isPending}
                className="rounded-md bg-amber-600 hover:bg-amber-500 text-slate-950 font-medium px-3 py-1.5 text-xs shadow"
              >
                {pauseMutation.isPending ? "Pausing…" : "Pause Generation"}
              </button>
            )}

            {(r.status === "paused" || r.status === "errored" || r.status === "failed" || r.status === "cancelled") && (
              <button
                onClick={() => resumeMutation.mutate()}
                disabled={resumeMutation.isPending}
                className="rounded-md bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold px-3 py-1.5 text-xs shadow"
              >
                {resumeMutation.isPending ? "Resuming…" : "Resume Generation"}
              </button>
            )}

            {r.status !== "completed" && r.status !== "cancelled" && (
              <button
                onClick={() => cancelMutation.mutate()}
                disabled={cancelMutation.isPending}
                className="rounded-md bg-rose-950 hover:bg-rose-900 text-rose-200 font-medium px-3 py-1.5 text-xs border border-rose-800"
              >
                {cancelMutation.isPending ? "Cancelling…" : "Cancel Run"}
              </button>
            )}

            <button
              onClick={() => setShowBibleModal(true)}
              className="rounded-md bg-slate-800 hover:bg-slate-700 text-amber-200 font-medium px-3 py-1.5 text-xs border border-slate-700"
            >
              📖 Story Bible Editor
            </button>

            <button
              onClick={() => {
                setTempVal(r.config?.temperature ?? 0.7);
                setMaxReviseVal(r.maxRevisePasses);
                setShowSettingsModal(true);
              }}
              className="rounded-md bg-slate-800 hover:bg-slate-700 text-slate-200 font-medium px-3 py-1.5 text-xs border border-slate-700"
            >
              ⚙️ Settings
            </button>

            <button
              onClick={() => {
                setEditedPremise(r.premise);
                setIsEditingPremise((s) => !s);
              }}
              className="rounded-md bg-slate-800 hover:bg-slate-700 text-slate-200 font-medium px-3 py-1.5 text-xs border border-slate-700"
            >
              {isEditingPremise ? "Close Edit" : "Edit Premise"}
            </button>

            <button
              onClick={() => {
                if (confirm("Are you sure you want to delete this novel run?")) {
                  deleteMutation.mutate();
                }
              }}
              disabled={deleteMutation.isPending}
              className="rounded-md bg-rose-950/60 hover:bg-rose-900 text-rose-400 font-medium px-3 py-1.5 text-xs border border-rose-900"
            >
              {deleteMutation.isPending ? "Deleting…" : "Delete Run"}
            </button>
          </div>
        </div>

        <div className="flex items-start gap-4">
          {/* Cover Art Banner / Thumbnail */}
          <div className="relative group shrink-0">
            {r.coverUrl ? (
              <img
                src={resolveApiUrl(r.coverUrl)}
                alt="Novel Cover"
                className="w-24 h-auto max-h-36 object-contain rounded-lg border border-slate-700 shadow-md bg-slate-950"
              />
            ) : (
              <div className="w-20 h-32 bg-slate-900 border border-slate-700 rounded-lg flex flex-col items-center justify-center text-center p-2">
                <span className="text-xl mb-1">🎨</span>
                <span className="text-[10px] text-slate-400 font-semibold uppercase tracking-wider">No Cover</span>
              </div>
            )}
            <button
              onClick={() => generateCoverMutation.mutate()}
              disabled={generateCoverMutation.isPending}
              className="mt-2 w-full rounded bg-amber-500/10 hover:bg-amber-500/20 text-amber-300 border border-amber-500/40 text-[10px] font-semibold py-1 px-1 text-center transition-colors"
            >
              {generateCoverMutation.isPending ? "Generating…" : "🎨 Cover Art"}
            </button>
          </div>

          <div className="space-y-2 flex-1">
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold text-amber-100">
                {bible?.title ? bible.title : `${r.genreKey} novel`}
              </h1>
              {r.status === "running" && (
                <span className="inline-flex items-center gap-1.5 rounded-full bg-blue-950 px-2.5 py-0.5 text-xs font-medium text-blue-400 border border-blue-800 animate-pulse">
                  <span className="h-1.5 w-1.5 rounded-full bg-blue-400"></span>
                  Drafting Active
                </span>
              )}
              {r.status === "checkpoint_pending" && (
                <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-950 px-2.5 py-0.5 text-xs font-medium text-amber-400 border border-amber-800">
                  ⚡ Checkpoint Pending
                </span>
              )}
              {r.status === "paused" && (
                <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-950 px-2.5 py-0.5 text-xs font-medium text-amber-300 border border-amber-800">
                  ⏸ Paused
                </span>
              )}
            </div>

            {isEditingPremise ? (
              <div className="space-y-2 pt-1">
                <textarea
                  rows={3}
                  value={editedPremise}
                  onChange={(e) => setEditedPremise(e.target.value)}
                  className="w-full rounded-md bg-slate-950 border border-slate-700 px-3 py-2 text-sm focus:border-amber-400 focus:outline-none"
                />
                <div className="flex justify-end gap-2">
                  <button
                    onClick={() => setIsEditingPremise(false)}
                    className="px-3 py-1 text-xs text-slate-400 hover:text-slate-200"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => updatePremiseMutation.mutate()}
                    disabled={updatePremiseMutation.isPending}
                    className="rounded bg-amber-500 hover:bg-amber-400 text-slate-950 px-3 py-1 text-xs font-medium"
                  >
                    Save Premise
                  </button>
                </div>
              </div>
            ) : (
              <p className="text-slate-300 text-sm leading-relaxed">
                {bible?.logline ? bible.logline : r.premise}
              </p>
            )}

            <div className="text-xs text-slate-500 font-mono flex items-center gap-3 flex-wrap pt-1">
              <span>run: {r.id.slice(0, 8)}…</span>
              <span>
                status: <strong className="text-amber-300 font-medium">{r.status}</strong>
              </span>
              <span>
                model: <strong className="text-slate-300">{r.model}</strong>
              </span>
              <span>
                temp: <strong className="text-amber-400">{r.config?.temperature ?? 0.7}</strong>
              </span>
            </div>

            {r.errorMessage && (
              <div className="rounded-md bg-rose-950 border border-rose-800 px-4 py-3 text-sm text-rose-200 mt-2">
                {r.errorMessage}
              </div>
            )}
          </div>
        </div>
      </header>

      <section className="rounded-lg border border-slate-800 bg-slate-900/40 p-5 space-y-3">
        <div className="flex items-center justify-between text-sm">
          <span className="text-slate-400">Progress</span>
          <span className="font-mono text-slate-200 font-medium">
            {(r.currentChapter ?? 0).toString().padStart(3, "0")} /{" "}
            {r.targetChapters} chapters ({pct}%)
          </span>
        </div>
        <div className="h-2 rounded-full bg-slate-800 overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-amber-400 to-amber-600 transition-[width] duration-500"
            style={{ width: `${pct}%` }}
          />
        </div>
        <div className="text-xs text-slate-500 grid grid-cols-2 md:grid-cols-4 gap-2 pt-2">
          <Stat label="Words / ch" value={r.wordsPerChapter.toString()} />
          <Stat label="Checkpoint every" value={`${r.checkpointEvery} ch`} />
          <Stat label="Max revise" value={r.maxRevisePasses.toString()} />
          <Stat label="Register" value={r.register} />
        </div>
      </section>

      {/* Checkpoint Approval & Review Control Panel */}
      {(r.status === "checkpoint_pending" || r.status === "paused") && (
        <CheckpointPanel runId={r.id} chapter={r.currentChapter ?? 0} onDecision={invalidate} />
      )}

      <section className="space-y-4">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <h2 className="text-lg font-semibold">Chapters</h2>
            <button
              onClick={() => {
                setInsertNo((r.currentChapter ?? 0) + 1);
                setShowInsertModal(true);
              }}
              className="rounded-md bg-amber-500/10 hover:bg-amber-500/20 text-amber-300 border border-amber-500/40 font-medium px-3 py-1 text-xs"
            >
              + Insert Chapter
            </button>
          </div>

          <div className="w-full sm:w-64">
            <input
              type="text"
              placeholder="Search chapters…"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full rounded-md bg-slate-950 border border-slate-800 px-3 py-1.5 text-xs text-slate-200 focus:border-amber-400 focus:outline-none"
            />
          </div>
        </div>

        {chapters.isLoading ? (
          <div className="text-slate-400 text-sm">Loading chapters…</div>
        ) : chapters.error ? (
          <div className="rounded-md bg-rose-950 border border-rose-800 px-4 py-3 text-sm text-rose-200">
            {(chapters.error as Error).message}
          </div>
        ) : filteredChapters.length === 0 ? (
          <div className="rounded-lg border border-dashed border-slate-800 px-6 py-8 text-center text-slate-500 text-sm">
            {searchQuery ? "No matching chapters found." : "No chapters drafted yet."}
          </div>
        ) : (
          <ul className="divide-y divide-slate-800 rounded-lg border border-slate-800 bg-slate-900/30">
            {filteredChapters.map((c, idx) => (
              <li key={c.id} className="px-4 py-3 flex items-center gap-3 hover:bg-slate-900/50">
                <div className="flex flex-col gap-0.5">
                  <button
                    onClick={() => moveChapter(idx, "up")}
                    disabled={idx === 0 || reorderMutation.isPending}
                    className="text-[10px] text-slate-500 hover:text-amber-300 disabled:opacity-20"
                    title="Move chapter up"
                  >
                    ▲
                  </button>
                  <button
                    onClick={() => moveChapter(idx, "down")}
                    disabled={idx === chapterList.length - 1 || reorderMutation.isPending}
                    className="text-[10px] text-slate-500 hover:text-amber-300 disabled:opacity-20"
                    title="Move chapter down"
                  >
                    ▼
                  </button>
                </div>
                <span className="font-mono text-xs text-slate-500 w-10">
                  #{c.chapterNo.toString().padStart(3, "0")}
                </span>
                <Link
                  to={`/novels/${r.id}/chapter/${c.chapterNo}`}
                  className="flex-1 text-slate-100 hover:text-amber-300 truncate font-medium text-sm"
                >
                  {c.title ?? `Chapter ${c.chapterNo}`}
                </Link>
                <span className="text-xs text-slate-400 font-mono">
                  {c.wordCount ?? 0} words
                </span>
                <span className="text-xs text-slate-500">
                  {c.revisePasses ?? 0} revisions
                </span>
                <Link
                  to={`/novels/${r.id}/chapter/${c.chapterNo}`}
                  className="text-xs text-amber-400 hover:text-amber-300 font-medium px-2 py-1 bg-slate-800 rounded border border-slate-700"
                >
                  Read & Edit →
                </Link>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* Structured Story Bible Editor Modal */}
      {showBibleModal && (
        <StoryBibleEditorModal
          runId={runId}
          markdown={bibleMd.data ?? ""}
          bibleData={bibleFull.data?.bible}
          isLoading={bibleMd.isLoading || bibleFull.isLoading}
          onClose={() => setShowBibleModal(false)}
          onSaved={() => {
            qc.invalidateQueries({ queryKey: ["bibleMd", runId] });
            qc.invalidateQueries({ queryKey: ["bibleFull", runId] });
            invalidate();
          }}
        />
      )}

      {/* Settings Modal */}
      {showSettingsModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/80 p-4 backdrop-blur-sm">
          <div className="w-full max-w-md rounded-lg border border-slate-800 bg-slate-900 p-6 space-y-5 shadow-2xl">
            <div className="flex items-center justify-between border-b border-slate-800 pb-3">
              <h2 className="text-lg font-semibold text-amber-200">⚙️ Run Settings</h2>
              <button
                onClick={() => setShowSettingsModal(false)}
                className="text-slate-400 hover:text-slate-200 text-sm font-semibold"
              >
                ✕
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <div className="flex justify-between items-center text-xs uppercase tracking-wide text-slate-400 mb-2">
                  <span>LLM Temperature</span>
                  <span className="font-mono text-amber-400 font-bold text-sm">
                    {tempVal.toFixed(2)}
                  </span>
                </div>
                <input
                  type="range"
                  min="0.1"
                  max="1.0"
                  step="0.05"
                  value={tempVal}
                  onChange={(e) => setTempVal(parseFloat(e.target.value))}
                  className="w-full accent-amber-500 cursor-pointer"
                />
                <p className="text-[11px] text-slate-500 mt-1">
                  Lower values (0.2–0.5) produce strict factual prose; higher values (0.7–1.0) produce creative dialogue & detailed scenes.
                </p>
              </div>

              <div>
                <label className="block text-xs uppercase tracking-wide text-slate-400 mb-1">
                  Max Revise Passes
                </label>
                <input
                  type="number"
                  min={0}
                  max={5}
                  value={maxReviseVal}
                  onChange={(e) => setMaxReviseVal(parseInt(e.target.value, 10))}
                  className="w-full rounded-md bg-slate-950 border border-slate-700 px-3 py-2 text-sm text-slate-100"
                />
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-2 border-t border-slate-800">
              <button
                onClick={() => setShowSettingsModal(false)}
                className="px-4 py-1.5 text-xs text-slate-400 hover:text-slate-200"
              >
                Cancel
              </button>
              <button
                onClick={() => updateSettingsMutation.mutate()}
                disabled={updateSettingsMutation.isPending}
                className="rounded bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold px-4 py-1.5 text-xs shadow"
              >
                {updateSettingsMutation.isPending ? "Saving…" : "Save Settings"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Insert Chapter Modal */}
      {showInsertModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/80 p-4 backdrop-blur-sm">
          <div className="w-full max-w-lg rounded-lg border border-slate-800 bg-slate-900 p-6 space-y-4 shadow-2xl">
            <div className="flex items-center justify-between border-b border-slate-800 pb-3">
              <h2 className="text-lg font-semibold text-amber-200">+ Insert Chapter</h2>
              <button
                onClick={() => setShowInsertModal(false)}
                className="text-slate-400 hover:text-slate-200 text-sm font-semibold"
              >
                ✕
              </button>
            </div>

            <div className="space-y-3">
              <div>
                <label className="block text-xs uppercase tracking-wide text-slate-400 mb-1">
                  Chapter Number
                </label>
                <input
                  type="number"
                  min={1}
                  value={insertNo}
                  onChange={(e) => setInsertNo(parseInt(e.target.value, 10))}
                  className="w-full rounded-md bg-slate-950 border border-slate-700 px-3 py-2 text-sm text-slate-100"
                />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-wide text-slate-400 mb-1">
                  Chapter Title (Optional)
                </label>
                <input
                  type="text"
                  placeholder="e.g. রহস্যের সূত্রপাত"
                  value={insertTitle}
                  onChange={(e) => setInsertTitle(e.target.value)}
                  className="w-full rounded-md bg-slate-950 border border-slate-700 px-3 py-2 text-sm text-slate-100"
                />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-wide text-slate-400 mb-1">
                  Custom Prose / Draft (Optional)
                </label>
                <textarea
                  rows={6}
                  placeholder="Write or paste Bengali chapter prose here..."
                  value={insertContent}
                  onChange={(e) => setInsertContent(e.target.value)}
                  className="w-full rounded-md bg-slate-950 border border-slate-700 p-3 text-sm font-mono text-slate-100"
                />
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-2 border-t border-slate-800">
              <button
                onClick={() => setShowInsertModal(false)}
                className="px-4 py-1.5 text-xs text-slate-400 hover:text-slate-200"
              >
                Cancel
              </button>
              <button
                onClick={() => insertChapterMutation.mutate()}
                disabled={insertChapterMutation.isPending}
                className="rounded bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold px-4 py-1.5 text-xs shadow"
              >
                {insertChapterMutation.isPending ? "Inserting…" : "Insert Chapter"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col">
      <span className="text-slate-500 uppercase tracking-wide text-[10px]">
        {label}
      </span>
      <span className="text-slate-200 font-medium">{value}</span>
    </div>
  );
}

function CheckpointPanel({
  runId,
  chapter,
  onDecision,
}: {
  runId: string;
  chapter: number;
  onDecision: () => void;
}) {
  const [isPending, setIsPending] = useState(false);

  const decide = async (decision: "approve" | "revise" | "abort") => {
    setIsPending(true);
    try {
      await api.decideCheckpoint(runId, chapter, decision);
      onDecision();
    } catch (err) {
      alert(`Checkpoint action failed: ${(err as Error).message}`);
    } finally {
      setIsPending(false);
    }
  };

  return (
    <section className="rounded-lg border border-amber-600/80 bg-amber-950/70 p-5 space-y-4 shadow-xl">
      <div className="flex items-center justify-between">
        <h2 className="text-amber-200 font-semibold text-base flex items-center gap-2">
          <span>⚡</span> Checkpoint Reached (Chapter {chapter})
        </h2>
        <span className="text-xs font-mono bg-amber-900 text-amber-300 px-2.5 py-1 rounded border border-amber-700 font-medium">
          Human Approval Required
        </span>
      </div>
      <p className="text-sm text-amber-100/90 leading-relaxed">
        The AI workflow has reached a checkpoint and paused generation for your review. Read the drafted chapters above, then click <strong>Approve & Continue</strong> to resume drafting next chapters.
      </p>
      <div className="flex flex-wrap items-center gap-3 pt-1">
        <button
          onClick={() => decide("approve")}
          disabled={isPending}
          className="rounded-md bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-bold px-5 py-2 text-sm shadow transition-colors disabled:opacity-50"
        >
          {isPending ? "Processing…" : "✓ Approve & Continue"}
        </button>
        <button
          onClick={() => decide("revise")}
          disabled={isPending}
          className="rounded-md bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold px-4 py-2 text-sm shadow transition-colors disabled:opacity-50"
        >
          ↻ Request Revision
        </button>
        <button
          onClick={() => decide("abort")}
          disabled={isPending}
          className="rounded-md bg-rose-800 hover:bg-rose-700 text-slate-100 font-semibold px-4 py-2 text-sm shadow transition-colors disabled:opacity-50 border border-rose-600"
        >
          ✕ Cancel Run
        </button>
      </div>
    </section>
  );
}

function StoryBibleEditorModal({
  runId,
  markdown,
  bibleData,
  isLoading,
  onClose,
  onSaved,
}: {
  runId: string;
  markdown: string;
  bibleData?: Record<string, any>;
  isLoading: boolean;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [tab, setTab] = useState<"doc" | "editor">("editor");
  const [isSaving, setIsSaving] = useState(false);

  // Form state
  const [title, setTitle] = useState(bibleData?.title ?? "");
  const [logline, setLogline] = useState(bibleData?.logline ?? "");
  const [characters, setCharacters] = useState<any[]>(bibleData?.characters ?? []);
  const [locations, setLocations] = useState<any[]>(bibleData?.locations ?? []);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await api.updateBibleStructured(runId, {
        ...bibleData,
        title,
        logline,
        characters,
        locations,
      });
      onSaved();
      onClose();
    } catch (e: unknown) {
      alert(`Failed to save Story Bible: ${(e as Error).message}`);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/80 p-4 backdrop-blur-sm">
      <div className="w-full max-w-4xl max-h-[85vh] rounded-lg border border-slate-800 bg-slate-900 p-6 flex flex-col space-y-4 shadow-2xl overflow-hidden">
        <div className="flex items-center justify-between border-b border-slate-800 pb-3">
          <div className="flex items-center gap-4">
            <h2 className="text-lg font-semibold text-amber-200">Story Bible</h2>
            <div className="flex items-center gap-1 bg-slate-950 p-1 rounded-md border border-slate-800">
              <button
                onClick={() => setTab("editor")}
                className={`px-3 py-1 text-xs font-semibold rounded ${
                  tab === "editor" ? "bg-amber-500 text-slate-950" : "text-slate-400 hover:text-slate-200"
                }`}
              >
                Structured Editor
              </button>
              <button
                onClick={() => setTab("doc")}
                className={`px-3 py-1 text-xs font-semibold rounded ${
                  tab === "doc" ? "bg-amber-500 text-slate-950" : "text-slate-400 hover:text-slate-200"
                }`}
              >
                Markdown View
              </button>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-200 text-sm font-semibold px-2 py-1 rounded bg-slate-800"
          >
            ✕ Close
          </button>
        </div>

        <div className="flex-1 overflow-y-auto pr-2 space-y-4 text-sm text-slate-200">
          {isLoading ? (
            <div className="text-slate-400 text-sm">Loading Story Bible…</div>
          ) : tab === "doc" ? (
            <div className="prose-bn max-w-none text-slate-200">
              <ReactMarkdown>{markdown}</ReactMarkdown>
            </div>
          ) : (
            <div className="space-y-5">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs uppercase tracking-wide text-slate-400 mb-1">
                    Novel Title
                  </label>
                  <input
                    type="text"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    className="w-full rounded-md bg-slate-950 border border-slate-700 px-3 py-2 text-sm text-slate-100"
                  />
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-wide text-slate-400 mb-1">
                    Logline
                  </label>
                  <input
                    type="text"
                    value={logline}
                    onChange={(e) => setLogline(e.target.value)}
                    className="w-full rounded-md bg-slate-950 border border-slate-700 px-3 py-2 text-sm text-slate-100"
                  />
                </div>
              </div>

              <div>
                <h3 className="text-xs uppercase tracking-wide text-amber-400 font-semibold mb-2">
                  Characters ({characters.length})
                </h3>
                <div className="space-y-3">
                  {characters.map((c, idx) => (
                    <div key={idx} className="p-3 rounded-md bg-slate-950 border border-slate-800 grid grid-cols-1 sm:grid-cols-3 gap-2">
                      <input
                        type="text"
                        placeholder="Character Name"
                        value={c.name ?? ""}
                        onChange={(e) => {
                          const next = [...characters];
                          next[idx] = { ...next[idx], name: e.target.value };
                          setCharacters(next);
                        }}
                        className="rounded bg-slate-900 border border-slate-700 px-2 py-1 text-xs"
                      />
                      <input
                        type="text"
                        placeholder="Role (e.g. protagonist)"
                        value={c.role ?? ""}
                        onChange={(e) => {
                          const next = [...characters];
                          next[idx] = { ...next[idx], role: e.target.value };
                          setCharacters(next);
                        }}
                        className="rounded bg-slate-900 border border-slate-700 px-2 py-1 text-xs"
                      />
                      <input
                        type="text"
                        placeholder="Summary"
                        value={c.summary ?? ""}
                        onChange={(e) => {
                          const next = [...characters];
                          next[idx] = { ...next[idx], summary: e.target.value };
                          setCharacters(next);
                        }}
                        className="rounded bg-slate-900 border border-slate-700 px-2 py-1 text-xs"
                      />
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <h3 className="text-xs uppercase tracking-wide text-amber-400 font-semibold mb-2">
                  Locations ({locations.length})
                </h3>
                <div className="space-y-3">
                  {locations.map((loc, idx) => (
                    <div key={idx} className="p-3 rounded-md bg-slate-950 border border-slate-800 grid grid-cols-1 sm:grid-cols-2 gap-2">
                      <input
                        type="text"
                        placeholder="Location Name"
                        value={loc.name ?? ""}
                        onChange={(e) => {
                          const next = [...locations];
                          next[idx] = { ...next[idx], name: e.target.value };
                          setLocations(next);
                        }}
                        className="rounded bg-slate-900 border border-slate-700 px-2 py-1 text-xs"
                      />
                      <input
                        type="text"
                        placeholder="Description"
                        value={loc.description ?? ""}
                        onChange={(e) => {
                          const next = [...locations];
                          next[idx] = { ...next[idx], description: e.target.value };
                          setLocations(next);
                        }}
                        className="rounded bg-slate-900 border border-slate-700 px-2 py-1 text-xs"
                      />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        {tab === "editor" && (
          <div className="flex justify-end gap-2 pt-3 border-t border-slate-800">
            <button
              onClick={onClose}
              className="px-4 py-1.5 text-xs text-slate-400 hover:text-slate-200"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={isSaving}
              className="rounded bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold px-4 py-1.5 text-xs shadow"
            >
              {isSaving ? "Saving…" : "Save Structured Bible"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
